import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { getListingMeta } from "../lib/api";

const BUDGETS = [
  { label: "Any budget", value: "any" },
  { label: "Under $4M", value: "0-4000000" },
  { label: "$4M – $6M", value: "4000000-6000000" },
  { label: "$6M – $10M", value: "6000000-10000000" },
  { label: "$10M and above", value: "10000000-999000000" },
  { label: "Rentals", value: "rent" },
];

const TYPES = ["Any type", "Terrace", "Corner Terrace", "Semi-Detached", "Detached", "Good Class Bungalow"];

export const DiscoveryBar = () => {
  const navigate = useNavigate();
  const [district, setDistrict] = useState("any");
  const [type, setType] = useState("Any type");
  const [budget, setBudget] = useState("any");
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    getListingMeta()
      .then((meta) => setDistricts(meta.districts))
      .catch(() => setDistricts([]));
  }, []);

  const search = () => {
    const params = new URLSearchParams();
    if (district !== "any") params.set("district", district);
    if (type !== "Any type") params.set("type", type);
    if (budget === "rent") {
      params.set("mode", "rent");
    } else if (budget !== "any") {
      const [min, max] = budget.split("-");
      params.set("min_price", min);
      params.set("max_price", max);
    }
    navigate(`/properties${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <div
      id="discovery"
      className="relative z-20 mx-auto -mt-24 w-full max-w-6xl scroll-mt-32 px-6 lg:px-8"
      data-testid="discovery-bar"
    >
      <div className="grid grid-cols-1 border border-ivory/15 bg-[#1f1f1f] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
        <div className="border-b border-ivory/10 px-6 py-5 sm:border-b-0 sm:border-r">
          <label className="eyebrow text-ivory/50" htmlFor="discovery-district">Location / District</label>
          <Select value={district} onValueChange={setDistrict}>
            <SelectTrigger
              id="discovery-district"
              data-testid="discovery-district-select"
              className="mt-1 h-auto border-0 bg-transparent p-0 font-serif text-xl text-ivory shadow-none focus:ring-0"
            >
              <SelectValue placeholder="All districts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All districts</SelectItem>
              {districts.map((d) => (
                <SelectItem key={d.code} value={d.code}>
                  {d.code} — {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-b border-ivory/10 px-6 py-5 sm:border-b-0 sm:border-r">
          <label className="eyebrow text-ivory/50" htmlFor="discovery-type">Property Type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger
              id="discovery-type"
              data-testid="discovery-type-select"
              className="mt-1 h-auto border-0 bg-transparent p-0 font-serif text-xl text-ivory shadow-none focus:ring-0"
            >
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-b border-ivory/10 px-6 py-5 lg:border-b-0 lg:border-r">
          <label className="eyebrow text-ivory/50" htmlFor="discovery-budget">Budget</label>
          <Select value={budget} onValueChange={setBudget}>
            <SelectTrigger
              id="discovery-budget"
              data-testid="discovery-budget-select"
              className="mt-1 h-auto border-0 bg-transparent p-0 font-serif text-xl text-ivory shadow-none focus:ring-0"
            >
              <SelectValue placeholder="Any budget" />
            </SelectTrigger>
            <SelectContent>
              {BUDGETS.map((b) => (
                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          type="button"
          onClick={search}
          data-testid="discovery-search-button"
          className="group flex items-center justify-center gap-3 bg-brand px-10 py-6 text-sm font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-brand-dark"
        >
          <Search className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          Search
        </button>
      </div>
      <p className="mt-3 text-center text-xs text-ivory/40" data-testid="discovery-demo-note">
        Demonstration search over a small, clearly-labelled sample catalogue.
      </p>
    </div>
  );
};
